import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import Layout from '../../layout';
import Container from '@/components/ui/container';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import TableSkeleton from '@/components/ui/tableSkeleton';
import axiosInstance from '../../lib/axiosInstance';
import { FaStar } from 'react-icons/fa6';
import {
    RiStarSmileLine,
    RiRefreshLine,
    RiCheckLine,
    RiCloseLine,
    RiDeleteBinLine,
    RiUser3Line,
    RiTimeLine
} from 'react-icons/ri';

const ListReviews = () => {
    const [reviews, setReviews] = useState([]);
    const [stats, setStats] = useState({
        total: 0,
        pending: 0,
        approved: 0,
        rejected: 0,
        averageRating: 0
    });
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [filterStatus, setFilterStatus] = useState('all');

    useEffect(() => {
        fetchReviews();
        fetchStats();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterStatus]);

    const fetchStats = async () => {
        try {
            const response = await axiosInstance.get('/reviews/admin/stats');
            if (response.data.success) {
                setStats(response.data.data || {});
            }
        } catch (error) {
            console.error('Error fetching stats:', error);
        }
    };

    const fetchReviews = async () => {
        try {
            setLoading(true);
            setRefreshing(true);
            const response = await axiosInstance.get('/reviews/admin/all', {
                params: { status: filterStatus }
            });

            if (response.data.success && response.data.data) {
                setReviews(response.data.data);
            } else {
                setReviews([]);
            }
        } catch (error) {
            console.error('Error fetching reviews:', error.response?.data || error.message);
            toast.error(error.response?.data?.message || 'Failed to fetch reviews');
            setReviews([]);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const handleRefresh = async () => {
        await fetchStats();
        await fetchReviews();
    };

    const handleStatusChange = async (reviewID, newStatus) => {
        try {
            await axiosInstance.put(`/reviews/admin/${reviewID}/status`, {
                status: newStatus
            });
            toast.success(`Review ${newStatus} successfully!`);
            fetchReviews();
            fetchStats();
        } catch (error) {
            console.error('Error updating review:', error);
            toast.error('Failed to update review status');
        }
    };

    const handleDeleteReview = async (reviewID) => {
        if (!window.confirm('Are you sure you want to delete this review? This action cannot be undone.')) {
            return;
        }

        try {
            await axiosInstance.delete(`/reviews/admin/${reviewID}`);
            toast.success('Review deleted successfully!');
            fetchReviews();
            fetchStats();
        } catch (error) {
            console.error('Error deleting review:', error);
            toast.error('Failed to delete review');
        }
    };

    const renderStars = (rating) => {
        return (
            <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                    <FaStar
                        key={star}
                        className={star <= rating ? 'text-amber-400' : 'text-slate-200'}
                        size={14}
                    />
                ))}
                <span className="ml-1 text-xs font-bold text-slate-700">{rating || 0}</span>
            </div>
        );
    };

    return (
        <Layout title="All Reviews" active="admin-reviews-list">
            <div className="min-h-screen bg-slate-50/50 p-6 space-y-6">
                <Container>
                    {/* Header Section */}
                    <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div>
                            <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
                                <RiStarSmileLine className="text-amber-500" /> Customer Reviews & Ratings
                            </h1>
                            <p className="text-slate-500 text-sm mt-1">
                                Moderated customer feedback, ratings, and review approvals.
                            </p>
                        </div>
                        <Button
                            onClick={handleRefresh}
                            disabled={refreshing}
                            variant="outline"
                            className="bg-white hover:bg-slate-100 border-slate-200 text-slate-700 shadow-sm"
                        >
                            <RiRefreshLine className={`w-4 h-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
                        </Button>
                    </div>

                    {/* Stats Cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
                        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
                            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Total Reviews</p>
                            <p className="text-2xl font-extrabold text-slate-900 mt-1">{stats.total || 0}</p>
                        </div>

                        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
                            <p className="text-xs font-semibold text-amber-600 uppercase tracking-wider">Pending Approval</p>
                            <p className="text-2xl font-extrabold text-amber-600 mt-1">{stats.pending || 0}</p>
                        </div>

                        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
                            <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wider">Approved</p>
                            <p className="text-2xl font-extrabold text-emerald-600 mt-1">{stats.approved || 0}</p>
                        </div>

                        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
                            <p className="text-xs font-semibold text-rose-600 uppercase tracking-wider">Rejected</p>
                            <p className="text-2xl font-extrabold text-rose-600 mt-1">{stats.rejected || 0}</p>
                        </div>

                        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
                            <p className="text-xs font-semibold text-purple-600 uppercase tracking-wider">Average Rating</p>
                            <p className="text-2xl font-extrabold text-purple-600 mt-1 flex items-center gap-1">
                                {typeof stats.averageRating === 'number' ? stats.averageRating.toFixed(1) : '0.0'}
                                <FaStar className="w-5 h-5 text-amber-400 inline" />
                            </p>
                        </div>
                    </div>

                    {/* Filter Pills */}
                    <div className="flex items-center gap-2 mb-6">
                        {[
                            { key: 'all', label: 'All Reviews' },
                            { key: 'pending', label: 'Pending' },
                            { key: 'approved', label: 'Approved' },
                            { key: 'rejected', label: 'Rejected' },
                        ].map((btn) => (
                            <button
                                key={btn.key}
                                onClick={() => setFilterStatus(btn.key)}
                                className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all ${
                                    filterStatus === btn.key
                                        ? 'bg-slate-900 text-white shadow-sm'
                                        : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
                                }`}
                            >
                                {btn.label}
                            </button>
                        ))}
                    </div>

                    {/* Reviews Table Card */}
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                            <h3 className="text-base font-semibold text-slate-800">Reviews ({reviews.length})</h3>
                        </div>

                        <div className="overflow-x-auto">
                            <Table className="w-full">
                                <TableHeader>
                                    <TableRow className="bg-slate-50/80 border-b border-slate-200">
                                        <TableHead className="px-6 py-3.5 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Product</TableHead>
                                        <TableHead className="px-6 py-3.5 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">User</TableHead>
                                        <TableHead className="px-6 py-3.5 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Rating</TableHead>
                                        <TableHead className="px-6 py-3.5 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Comment</TableHead>
                                        <TableHead className="px-6 py-3.5 text-center text-xs font-bold text-slate-600 uppercase tracking-wider">Status</TableHead>
                                        <TableHead className="px-6 py-3.5 text-center text-xs font-bold text-slate-600 uppercase tracking-wider">Date</TableHead>
                                        <TableHead className="px-6 py-3.5 text-center text-xs font-bold text-slate-600 uppercase tracking-wider">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody className="divide-y divide-slate-100">
                                    {loading ? (
                                        <TableSkeleton columns={7} rows={5} />
                                    ) : reviews.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={7} className="px-6 py-12 text-center">
                                                <div className="flex flex-col items-center justify-center">
                                                    <RiStarSmileLine className="w-12 h-12 text-slate-300 mb-3" />
                                                    <p className="text-slate-700 font-semibold text-base">No reviews found</p>
                                                    <p className="text-slate-400 text-xs mt-1">No feedback matching current status filter.</p>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        reviews.map((review) => (
                                            <TableRow key={review.reviewID} className="hover:bg-slate-50/80 transition-colors">
                                                <TableCell className="px-6 py-4 font-semibold text-slate-900 max-w-[200px] truncate">
                                                    {review.productName || review.productID || 'General Product'}
                                                </TableCell>
                                                <TableCell className="px-6 py-4">
                                                    <div className="flex items-center gap-2">
                                                        <RiUser3Line className="text-slate-400 w-4 h-4" />
                                                        <div>
                                                            <p className="text-sm font-semibold text-slate-900">{review.username || 'Anonymous'}</p>
                                                            <p className="text-xs text-slate-400">{review.emailID || ''}</p>
                                                        </div>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="px-6 py-4">
                                                    {renderStars(review.rating)}
                                                </TableCell>
                                                <TableCell className="px-6 py-4 max-w-[250px]">
                                                    <p className="text-xs text-slate-700 line-clamp-2">
                                                        {review.comment || 'No written comment'}
                                                    </p>
                                                </TableCell>
                                                <TableCell className="px-6 py-4 text-center">
                                                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${
                                                        review.status === 'approved'
                                                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                                            : review.status === 'rejected'
                                                            ? 'bg-rose-50 text-rose-700 border border-rose-200'
                                                            : 'bg-amber-50 text-amber-700 border border-amber-200'
                                                    }`}>
                                                        {review.status || 'pending'}
                                                    </span>
                                                </TableCell>
                                                <TableCell className="px-6 py-4 text-center text-xs text-slate-500">
                                                    <div className="flex items-center justify-center gap-1">
                                                        <RiTimeLine className="text-slate-400" />
                                                        {review.createdAt ? new Date(review.createdAt).toLocaleDateString() : '-'}
                                                    </div>
                                                </TableCell>
                                                <TableCell className="px-6 py-4 text-center">
                                                    <div className="flex items-center justify-center gap-2">
                                                        {review.status !== 'approved' && (
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => handleStatusChange(review.reviewID, 'approved')}
                                                                className="text-emerald-600 border-emerald-200 hover:bg-emerald-50 text-xs px-2.5 py-1"
                                                            >
                                                                <RiCheckLine className="w-3.5 h-3.5 mr-1" /> Approve
                                                            </Button>
                                                        )}
                                                        {review.status !== 'rejected' && (
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => handleStatusChange(review.reviewID, 'rejected')}
                                                                className="text-amber-600 border-amber-200 hover:bg-amber-50 text-xs px-2.5 py-1"
                                                            >
                                                                <RiCloseLine className="w-3.5 h-3.5 mr-1" /> Reject
                                                            </Button>
                                                        )}
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => handleDeleteReview(review.reviewID)}
                                                            className="text-rose-600 border-rose-200 hover:bg-rose-50 text-xs px-2.5 py-1"
                                                        >
                                                            <RiDeleteBinLine className="w-3.5 h-3.5" />
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </div>
                </Container>
            </div>
        </Layout>
    );
};

export default ListReviews;
